/* =========================================================================
   auth-context.tsx — Enterprise auth context with Supabase + passcode fallback.
   Supports: magic link, OAuth (Google, GitHub), email/password, passcode.
   ========================================================================= */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { supabase, isSupabaseConfigured } from "./supabase-client";
import { shouldForcePasscode } from "./auth-mode";
import type { User, Provider } from "@supabase/supabase-js";

// --- Passcode fallback (SHA-256 hashed, not reversibly encoded) ---
const ACCESS_CODE_HASH =
  "08603f9ba65abeb15c6623cf32cf3a1698da298334fcb89bbb451bf1b47db0ab";
const SESSION_KEY = "__sf_auth";

/* NOTE for linters and humans: throughout sha256, `| 0` is INTENTIONAL — it
   coerces to int32 with overflow wraparound, which SHA-256's modulo-2^32
   arithmetic requires. `Math.trunc` is NOT equivalent (no wraparound); do not
   "fix" these. (sonar S7767 false positive) */
const rotr = (n: number, x: number) => (x >>> n) | (x << (32 - n));
const ch = (x: number, y: number, z: number) => (x & y) ^ (~x & z);
const maj = (x: number, y: number, z: number) => (x & y) ^ (x & z) ^ (y & z);
const ep0 = (x: number) => rotr(2, x) ^ rotr(13, x) ^ rotr(22, x);
const ep1 = (x: number) => rotr(6, x) ^ rotr(11, x) ^ rotr(25, x);
const sig0 = (x: number) => rotr(7, x) ^ rotr(18, x) ^ (x >>> 3);
const sig1 = (x: number) => rotr(17, x) ^ rotr(19, x) ^ (x >>> 10);

/** Synchronous SHA-256 (RFC 6234) — browser + Node compatible. */
function sha256(message: string): string {
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
    0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
    0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
    0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];

  const utf8 = new TextEncoder().encode(message);
  const bitLen = utf8.length * 8;
  const padLen = Math.ceil((utf8.length + 9) / 64) * 64;
  const m = new Uint8Array(padLen);
  m.set(utf8);
  m[utf8.length] = 0x80;
  const view = new DataView(m.buffer);
  view.setUint32(padLen - 4, bitLen, false);

  let h0 = 0x6a09e667,
    h1 = 0xbb67ae85,
    h2 = 0x3c6ef372,
    h3 = 0xa54ff53a;
  let h4 = 0x510e527f,
    h5 = 0x9b05688c,
    h6 = 0x1f83d9ab,
    h7 = 0x5be0cd19;

  const w = new Uint32Array(64);
  for (let i = 0; i < padLen; i += 64) {
    for (let t = 0; t < 16; t++) w[t] = view.getUint32(i + t * 4, false);
    for (let t = 16; t < 64; t++)
      w[t] = (sig1(w[t - 2]) + w[t - 7] + sig0(w[t - 15]) + w[t - 16]) | 0;

    let a = h0,
      b = h1,
      c = h2,
      d = h3,
      e = h4,
      f = h5,
      g = h6,
      hh = h7;
    for (let t = 0; t < 64; t++) {
      const t1 = (hh + ep1(e) + ch(e, f, g) + K[t] + w[t]) | 0;
      const t2 = (ep0(a) + maj(a, b, c)) | 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }
    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
    h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0;
    h7 = (h7 + hh) | 0;
  }

  const hex = (n: number) => (n >>> 0).toString(16).padStart(8, "0");
  return (
    hex(h0) +
    hex(h1) +
    hex(h2) +
    hex(h3) +
    hex(h4) +
    hex(h5) +
    hex(h6) +
    hex(h7)
  );
}
const FORCE_PASSCODE = shouldForcePasscode(
  globalThis.location?.hostname ?? "",
  (globalThis as unknown as Record<string, unknown>).__sf_force_passcode,
  import.meta.env.VITE_FORCE_PASSCODE,
);

function isPasscodeAuthed(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function setPasscodeAuthed(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    /* private browsing fallback */
  }
}

function clearPasscodeAuth(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* noop */
  }
}

export interface AuthContextValue {
  user: User | null;
  email: string | undefined;
  loading: boolean;
  authed: boolean;
  usePasscodeFallback: boolean;
  productionAuthUnavailable: boolean;
  signInWithPassword: (
    email: string,
    password: string,
  ) => Promise<{ error?: Error }>;
  signInWithMagicLink: (email: string) => Promise<{ error?: Error }>;
  signInWithOAuth: (provider: Provider) => Promise<{ error?: Error }>;
  signOut: () => Promise<void>;
  verifyPasscode: (code: string) => boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}

/** Non-throwing variant for components that render in provider-less harnesses
    (unit tests, isolated screens). Null only ever occurs outside AuthProvider. */
export function useAuthSafe(): AuthContextValue | null {
  return useContext(AuthContext);
}

export function AuthProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const [user, setUser] = useState<User | null>(null);
  // A production deployment without its Supabase configuration must never
  // silently downgrade to the static client-side passcode. Local development
  // and explicitly forced passcode tests retain that convenience.
  const productionAuthUnavailable =
    import.meta.env.PROD && !isSupabaseConfigured() && !FORCE_PASSCODE;
  const usePasscodeFallback =
    !productionAuthUnavailable && (FORCE_PASSCODE || !isSupabaseConfigured());
  const [loading, setLoading] = useState(
    !usePasscodeFallback && !productionAuthUnavailable,
  );
  const [passcodeAuthed, setPasscodeAuthedState] = useState(isPasscodeAuthed);
  const authed = usePasscodeFallback ? passcodeAuthed : !!user;
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (
      productionAuthUnavailable ||
      FORCE_PASSCODE ||
      !isSupabaseConfigured()
    ) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted.current) return;
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted.current) return;
        setUser(session?.user ?? null);
      },
    );

    return () => {
      mounted.current = false;
      listener.subscription.unsubscribe();
    };
  }, [productionAuthUnavailable]);

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error: error ?? undefined };
    },
    [],
  );

  // No signUp: the workspace is invite-only — signups are disabled server-side and
  // accounts are provisioned via the Supabase admin API (2026-06-12).

  const signInWithMagicLink = useCallback(async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: globalThis.location.origin },
    });
    return { error: error ?? undefined };
  }, []);

  const signInWithOAuth = useCallback(async (provider: Provider) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: globalThis.location.origin },
    });
    return { error: error ?? undefined };
  }, []);

  const signOut = useCallback(async () => {
    if (!usePasscodeFallback) {
      await supabase.auth.signOut();
    }
    clearPasscodeAuth();
    setPasscodeAuthedState(false);
    setUser(null);
  }, [usePasscodeFallback]);

  const verifyPasscode = useCallback((code: string): boolean => {
    const ok = sha256(code.trim().toLowerCase()) === ACCESS_CODE_HASH;
    if (ok) {
      setPasscodeAuthed();
      setPasscodeAuthedState(true);
    }
    return ok;
  }, []);

  const logout = useCallback(() => {
    clearPasscodeAuth();
    setPasscodeAuthedState(false);
    setUser(null);
    if (!usePasscodeFallback) {
      supabase.auth.signOut().catch(() => {});
    }
  }, [usePasscodeFallback]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      email: user?.email,
      loading,
      authed,
      usePasscodeFallback,
      productionAuthUnavailable,
      signInWithPassword,
      signInWithMagicLink,
      signInWithOAuth,
      signOut,
      verifyPasscode,
      logout,
    }),
    [
      user,
      loading,
      authed,
      usePasscodeFallback,
      productionAuthUnavailable,
      signInWithPassword,
      signInWithMagicLink,
      signInWithOAuth,
      signOut,
      verifyPasscode,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
